import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent
} from '@mui/material'
import {
  Add,
  ShoppingCart,
  Repeat,
  TrendingUp,
  Assignment,
  CheckCircle,
  Warning
} from '@mui/icons-material'

import PurchaseOrdersTable from '../../components/PurchaseOrders/PurchaseOrdersTable'
import PurchaseOrderForm from '../../components/PurchaseOrders/PurchaseOrderForm'
import PurchaseOrderDetail from './PurchaseOrderDetailPage'
import useAuthStore from '../../store/authStore'
import RoleGuard from '../../components/Auth/RoleGuard'
import { usePurchaseOrders } from '../../hooks/usePurchaseOrders'
import { useQueryClient } from 'react-query'

const PurchaseOrdersPage = () => {
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null)
  const [editingPurchaseOrder, setEditingPurchaseOrder] = useState(null)

  const queryClient = useQueryClient()

  // Get summary data
  const { data: summaryData } = usePurchaseOrders({ page: 1, limit: 1 })

  // Refetch data when component mounts or window gains focus
  useEffect(() => {
    const handleFocus = () => {
      queryClient.refetchQueries(['purchaseOrders'])
    }

    window.addEventListener('focus', handleFocus)
    
    // Initial refetch
    queryClient.refetchQueries(['purchaseOrders'])

    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [queryClient])

  const handleCreateNew = () => {
    setEditingPurchaseOrder(null)
    setFormOpen(true)
  }

  const handleEdit = (purchaseOrder) => {
    setEditingPurchaseOrder(purchaseOrder)
    setFormOpen(true)
  }

  const handleView = (purchaseOrder) => {
    setSelectedPurchaseOrder(purchaseOrder)
    setDetailOpen(true)
  }

  const handleCloseForm = () => {
    setFormOpen(false)
    setEditingPurchaseOrder(null)
  }

  const handleCloseDetail = () => {
    setDetailOpen(false)
    setSelectedPurchaseOrder(null)
  }

  const counts = summaryData?.counts || {
    total: 0,
    POR_FACTURAR: 0,
    FACTURADA: 0,
    UNICA: 0,
    PERIODICA: 0
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Órdenes de Compra
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestiona las órdenes de compra y su facturación
          </Typography>
        </Box>
        
        <RoleGuard requireAdmin fallback={null}>
          <Button
            variant="contained"
            startIcon={<Add />}
            size="large"
            onClick={handleCreateNew}
            sx={{ borderRadius: 2 }}
          >
            Nueva OC
          </Button>
        </RoleGuard>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {counts.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total OC
                  </Typography>
                </Box>
                <ShoppingCart sx={{ fontSize: 40, color: 'primary.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {counts.POR_FACTURAR}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Por Facturar
                  </Typography>
                </Box>
                <Warning sx={{ fontSize: 40, color: 'warning.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {counts.FACTURADA}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Facturadas
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {counts.UNICA}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Únicas
                  </Typography>
                </Box>
                <ShoppingCart sx={{ fontSize: 40, color: 'warning.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="info.main">
                    {counts.PERIODICA}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Periódicas
                  </Typography>
                </Box>
                <Repeat sx={{ fontSize: 40, color: 'info.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Purchase Orders Table */}
      <PurchaseOrdersTable 
        onEdit={handleEdit}
        onView={handleView}
      />

      {/* Purchase Order Form Dialog */}
      <PurchaseOrderForm
        open={formOpen}
        onClose={handleCloseForm}
        purchaseOrder={editingPurchaseOrder}
      />

      {/* Purchase Order Detail Dialog */}
      {selectedPurchaseOrder && (
        <PurchaseOrderDetail
          open={detailOpen}
          onClose={handleCloseDetail}
          purchaseOrder={selectedPurchaseOrder}
        />
      )}
    </Box>
  )
}

export default PurchaseOrdersPage
